import {Card, Choice, DatePicker} from 'platform/components';
import {Grid, GridItem, Show, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {useReadCodelistQuery} from '@dms/api/core';
import {SaveSaleVehicleFinancingBody} from '@dms/api/vehicle';
import i18n from '@dms/i18n';

import {getApiDateString, parseDate} from 'shared/web';

import {FinancingTemplateDraft} from './types/FinancingTemplateDraft';
import {buildFinancingTemplateOptions} from './utils/buildFinancingTemplateOptions';
import {formatFinancingTemplateValidity} from './utils/formatFinancingTemplateValidity';

const UNKNOWN_BANK = '—';

interface FinancingBasicInformationProps {
  vehicleFinancingBody: SaveSaleVehicleFinancingBody | null;
  canReadTemplates: boolean;
  template: FinancingTemplateDraft | undefined;
  selectedTemplateId: string | null;
  templateOptions: ReturnType<typeof buildFinancingTemplateOptions>;
  areTemplatesLoading: boolean;
  isTemplateError: boolean;
  financialInstitutionError: string | null;
  isEditingAllowed: boolean;
  isTemplateChoiceBusy: boolean;
  isTemplateChoiceClearable: boolean;
  canOpenConfiguration: boolean;
  onTemplateChange: (templateId: string | null) => void;
  onOpenConfiguration: () => void;
  onChange: (vehicleFinancingBody: SaveSaleVehicleFinancingBody) => void;
}

export function FinancingBasicInformation(props: FinancingBasicInformationProps) {
  const formatDateTime = useDateTimeFormatter();
  const {
    data: financialInstitutions,
    isLoading: areFinancialInstitutionsLoading,
    isError: areFinancialInstitutionsError,
  } = useReadCodelistQuery({codelistId: 'leasing_and_credit_company'});

  const templateBank =
    financialInstitutions?.codes.find(
      (institution) => institution.codeId === props.template?.financialInstitutionCodeId
    )?.name ?? UNKNOWN_BANK;

  const updateBody = (changes: Partial<SaveSaleVehicleFinancingBody>) => {
    if (isNotNil(props.vehicleFinancingBody)) {
      props.onChange({...props.vehicleFinancingBody, ...changes});
    }
  };

  return (
    <Card
      title={i18n.t('general.labels.basicInformation')}
      titleIcon="action/info_outline"
      variant="inlineGrey"
      hasSeparator
    >
      <Grid columns={[1, 2, 3]}>
        <Show when={props.canReadTemplates}>
          <Choice
            data-testid="active-financing-template"
            label={i18n.t('page.vehicle.financingAndLeasing.activeTemplate')}
            labelActions={[
              {
                title: i18n.t('page.vehicle.financingAndLeasing.editConfiguration'),
                leftIcon: 'action/settings',
                isDisabled: !props.canOpenConfiguration,
                onClick: (event) => {
                  event.preventDefault();
                  props.onOpenConfiguration();
                },
              },
            ]}
            value={props.selectedTemplateId}
            options={props.templateOptions}
            isLoading={props.areTemplatesLoading}
            isDisabled={!props.isEditingAllowed || props.isTemplateChoiceBusy}
            isNotClearable={!props.isTemplateChoiceClearable}
            errorMessage={
              props.isTemplateError
                ? i18n.t('page.vehicle.financingAndLeasing.templateLoadError')
                : undefined
            }
            onChange={props.onTemplateChange}
          />
        </Show>
        <Show when={props.vehicleFinancingBody?.mode === 'MANUAL'}>
          <Choice
            label={i18n.t('entity.bank.labels.bank')}
            isRequired
            value={props.vehicleFinancingBody?.financialInstitutionCodeId ?? null}
            options={(financialInstitutions?.codes ?? [])
              .filter((code) => !code.isDisabled && isNotNilOrEmpty(code.name))
              .map((code) => ({
                value: code.codeId,
                label: code.name,
              }))}
            isLoading={areFinancialInstitutionsLoading}
            isDisabled={!props.isEditingAllowed || areFinancialInstitutionsError}
            errorMessage={
              areFinancialInstitutionsError
                ? i18n.t('general.errors.cannotLoadOptions')
                : (props.financialInstitutionError ?? undefined)
            }
            onChange={(financialInstitutionCodeId) => updateBody({financialInstitutionCodeId})}
          />
          <DatePicker
            label={i18n.t('entity.vehicleFinancingTemplate.labels.validTo')}
            value={parseDate(props.vehicleFinancingBody?.validTo) ?? null}
            isDisabled={!props.isEditingAllowed}
            onChange={(validTo) =>
              updateBody({validTo: isNotNil(validTo) ? getApiDateString(validTo) : null})
            }
          />
        </Show>
        <Show when={props.template}>
          <GridItem span={[1, 2]}>
            <Grid columns={[1, 2]} data-testid="financing-template-summary">
              <VStack spacing={2}>
                <Text size="small" color="secondary">
                  {i18n.t('entity.bank.labels.bank')}
                </Text>
                <Text size="small" alternative>
                  {templateBank}
                </Text>
              </VStack>
              <VStack spacing={2}>
                <Text size="small" color="secondary">
                  {i18n.t('entity.vehicleFinancingTemplate.labels.validityMode')}
                </Text>
                <Text size="small" alternative>
                  {formatFinancingTemplateValidity(
                    props.template?.validFrom,
                    props.template?.validTo,
                    formatDateTime
                  )}
                </Text>
              </VStack>
            </Grid>
          </GridItem>
        </Show>
      </Grid>
    </Card>
  );
}
